import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { getPatientById, getSingleUser } from '../api/Individual';
import { decryptData } from '../EncryptDecrypt';
import ClearstepSymptomChecker from './ClearstepSymptomChecker';

function parseStoredJson(storage, key) {
    try {
        const value = storage.getItem(key);
        return value ? JSON.parse(value) : null;
    } catch (error) {
        return null;
    }
}

function firstValue(...values) {
    return values.find((value) => value !== undefined && value !== null && value !== '');
}

function hasAnyValue(object, keys) {
    if (!object) return false;
    return keys.some((key) => object?.[key] !== undefined && object?.[key] !== null && object?.[key] !== '');
}

function normalizeGender(value) {
    if (!value) return '';
    const normalized = String(value).trim().toLowerCase();

    if (['m', 'male', 'man'].includes(normalized)) return 'male';
    if (['f', 'female', 'woman'].includes(normalized)) return 'female';

    if (normalized.startsWith('m')) return 'male';
    if (normalized.startsWith('f')) return 'female';

    return normalized;
}

function safeDecrypt(value) {
    if (value === undefined || value === null || value === '') return '';
    try {
        return decryptData(value) || value;
    } catch (error) {
        return value;
    }
}

function buildPatientView(profile, authUser, patientRecordId) {
    const source = profile || {};

    return {
        id: firstValue(
            source?.id,
            source?.patientId,
            source?.patient_id,
            source?.patientRefId,
            source?.patientRefID,
            patientRecordId
        ),
        dateOfBirth: safeDecrypt(firstValue(
            source?.dateOfBirth,
            source?.birthDate,
            source?.dob,
            source?.birthdate,
            authUser?.birthdate,
            authUser?.['custom:birthdate'],
            authUser?.['custom:dateOfBirth']
        )),
        gender: normalizeGender(safeDecrypt(firstValue(
            source?.gender,
            source?.sex,
            source?.administrativeGender,
            source?.birthSex,
            authUser?.gender,
            authUser?.['custom:gender'],
            authUser?.['custom:sex']
        ))),
    };
}

export default function ClearstepPage() {
    const navigate = useNavigate();
    const authUser = useSelector((state) => state?.auth?.user);

    const storedRelatedProfile = parseStoredJson(sessionStorage, 'relatedPersonProfile');
    const localLoggedInUserInfo = parseStoredJson(localStorage, 'loggedInUserInfo');
    const sessionLoggedInUserInfo = parseStoredJson(sessionStorage, 'loggedInUserInfo');

    const patientRecordId = firstValue(
        storedRelatedProfile?.patientRefId,
        storedRelatedProfile?.patientRefID,
        storedRelatedProfile?.patientId,
        storedRelatedProfile?.patient_id,
        storedRelatedProfile?.['custom:patient_id'],
        authUser?.['custom:patient_id'],
        localLoggedInUserInfo?.patientId,
        localLoggedInUserInfo?.patient_id,
        sessionLoggedInUserInfo?.patientId,
        sessionLoggedInUserInfo?.patient_id
    );

    const personRecordId = firstValue(
        storedRelatedProfile?.['custom:unique_id'],
        authUser?.['custom:unique_id'],
        localLoggedInUserInfo?.id,
        sessionLoggedInUserInfo?.id
    );

    const [patientProfile, setPatientProfile] = useState(null);
    const [personProfile, setPersonProfile] = useState(null);
    const [profileLoading, setProfileLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        async function loadProfiles() {
            setProfileLoading(true);

            const requests = [];

            if (patientRecordId) {
                requests.push(
                    getPatientById(patientRecordId)
                        .then((res) => {
                            if (!cancelled && res?.status === true) setPatientProfile(res?.data || null);
                        })
                        .catch(() => {
                            if (!cancelled) setPatientProfile(null);
                        })
                );
            }

            if (personRecordId) {
                requests.push(
                    getSingleUser({ id: personRecordId, type: 'Patient' })
                        .then((res) => {
                            if (!cancelled && res?.status === true) setPersonProfile(res?.data || null);
                        })
                        .catch(() => {
                            if (!cancelled) setPersonProfile(null);
                        })
                );
            }

            if (requests.length === 0) {
                setProfileLoading(false);
                return;
            }

            await Promise.allSettled(requests);
            if (!cancelled) setProfileLoading(false);
        }

        loadProfiles();

        return () => {
            cancelled = true;
        };
    }, [patientRecordId, personRecordId]);

    const patient = useMemo(() => {
        const candidateProfiles = [
            patientProfile,
            personProfile,
            localLoggedInUserInfo,
            sessionLoggedInUserInfo,
            storedRelatedProfile,
            authUser,
        ];

        const merged = candidateProfiles.reduce((acc, current) => ({ ...(current || {}), ...acc }), {});

        // Prefer the first source with demographic values, while still falling back to the merged object.
        const sourceWithDemographics = candidateProfiles.find((profile) =>
            hasAnyValue(profile, ['gender', 'sex', 'administrativeGender', 'birthSex', 'dateOfBirth', 'birthDate', 'dob', 'birthdate'])
        );

        return buildPatientView(
            { ...merged, ...(sourceWithDemographics || {}) },
            authUser,
            patientRecordId
        );
    }, [authUser, localLoggedInUserInfo, patientProfile, patientRecordId, personProfile, sessionLoggedInUserInfo, storedRelatedProfile]);

    const handleTriageComplete = ({ triage, complaint, conversationId, messages }) => {
        const payload = {
            patientId: patient?.id,
            completedAt: new Date().toISOString(),
            triage,
            complaint,
            conversationId,
            messages,
        };

        localStorage.setItem('latestClearstepTriage', JSON.stringify(payload));
    };

    return (
        <section className="common-listing" style={{ marginLeft: '100px' }}>
            <ClearstepSymptomChecker
                patient={patient}
                patientLoading={profileLoading}
                onTriageComplete={handleTriageComplete}
                onCancel={() => navigate('/app/home')}
            />
        </section>
    );
}
